'use client';

// Share + community CTAs — Figma Web app node 7:13.
//
// Intro copy plus two side-by-side channel cards (X, Telegram) with icon chip
// and descriptive copy.
import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { landingItemRevealDelay } from '@/components/support-mainnet/landing-item-reveal-delay';
import { LandingSectionReveal } from '@/components/support-mainnet/landing-section-reveal';
import { ShareChannelFieldBackground } from '@/components/support-mainnet/share-channel-field-background';
import { Icon } from '@/components/ui/icon';
import { NewTwitterIconSolid, TelegramIconSolid } from '@/components/ui/icon/brands';
import { emitShareEvent } from '@/lib/share';
import { COMMUNITY_CHANNELS, X_COMMUNITY_URL } from '@/lib/support-mainnet/data';
import { FEED_CARD_OUTLINE_INTERACTION } from '@/lib/ui/feed-card-chrome';
import { cn } from '@/lib/ui/utils';
const SHARE_CARD_HEIGHT = 'min-h-[234px] h-full' as const; /* ARBITRARY: Figma share card height */
const SHARE_CARD_RADIUS = 'rounded-[20px]' as const; /* ARBITRARY: Figma share card radius */
const SHARE_CARD_FILL = 'bg-[var(--gray-800)]' as const; /* ARBITRARY: Figma share card fill gray-800 */
const SHARE_CHIP_SIZE = 'size-12' as const; /* ARBITRARY: Figma icon chip 48px */
const SHARE_CHIP_RADIUS = 'rounded-xl' as const; /* ARBITRARY: Figma icon chip radius 12px */
const SHARE_CHIP_BG = 'bg-[var(--gray-700)]' as const; /* ARBITRARY: Figma icon chip gray-700 */
const SHARE_CARD_TEXT = 'text-[18px] leading-6' as const; /* ARBITRARY: Figma card copy 18px / 24px line height */
const SHARE_CARD_COPY_MAX = 'max-w-[400px]' as const; /* ARBITRARY: Figma card copy max width 400px */
const SHARE_SECTION_LEAD_MAX = 'max-w-[510px]' as const; /* ARBITRARY: Figma share section lead max width 510px */

const EXTERNAL_LINK_PROPS: {
  target: '_blank';
  rel: 'noopener noreferrer';
} = {
  target: '_blank',
  rel: 'noopener noreferrer'
};
type ShareChannelId = 'x' | 'telegram';
interface ShareChannelConfig {
  id: ShareChannelId;
  url: string;
  icon: typeof NewTwitterIconSolid;
  analyticsChannel: `follow_${ShareChannelId}`;
}
const SHARE_CHANNELS: readonly ShareChannelConfig[] = [{
  id: 'x',
  url: X_COMMUNITY_URL,
  icon: NewTwitterIconSolid,
  analyticsChannel: 'follow_x'
}, {
  id: 'telegram',
  url: COMMUNITY_CHANNELS[0]?.url ?? 'https://t.me/morfimarkets',
  icon: TelegramIconSolid,
  analyticsChannel: 'follow_telegram'
}] as const;
function ShareChannelCard(t0) {
  const $ = _c(24);
  const {
    channel,
    title,
    description
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("share-channel-card group relative isolate block h-full overflow-hidden border border-solid border-border-primary", FEED_CARD_OUTLINE_INTERACTION, SHARE_CARD_HEIGHT, SHARE_CARD_RADIUS, SHARE_CARD_FILL, "p-6");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = channel.url;
  let t3;
  if ($[1] !== channel.analyticsChannel) {
    t3 = () => {
      emitShareEvent({
        kind: "support_mainnet"
      }, channel.analyticsChannel);
    };
    $[1] = channel.analyticsChannel;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== channel.id) {
    t4 = <ShareChannelFieldBackground channel={channel.id} />;
    $[3] = channel.id;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div aria-hidden={true} className="share-channel-card-fade" />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span aria-hidden={true} className="share-channel-card__border-shimmer"><span className="share-channel-card__border-beam share-channel-card__border-beam--glow" /><span className="share-channel-card__border-beam" /></span>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("flex items-center justify-center", SHARE_CHIP_BG, SHARE_CHIP_SIZE, SHARE_CHIP_RADIUS);
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== channel.icon) {
    t8 = <div className={t7}><Icon className="size-6 text-foreground-primary" icon={channel.icon} /></div>;
    $[8] = channel.icon;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn(SHARE_CARD_COPY_MAX, "text-pretty text-foreground-disabled", SHARE_CARD_TEXT);
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== title) {
    t10 = <span className="share-channel-card-title font-medium text-foreground-primary">{title}</span>;
    $[11] = title;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== description || $[14] !== t10) {
    t11 = <p className={t9}>{t10}{description}</p>;
    $[13] = description;
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11 || $[17] !== t8) {
    t12 = <div className="relative z-10 flex h-full min-w-0 flex-col justify-between">{t8}{t11}</div>;
    $[16] = t11;
    $[17] = t8;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== channel.url || $[20] !== t12 || $[21] !== t3 || $[22] !== t4) {
    t13 = <a {...EXTERNAL_LINK_PROPS} className={t1} href={t2} onClick={t3}>{t4}{t5}{t6}{t12}</a>;
    $[19] = channel.url;
    $[20] = t12;
    $[21] = t3;
    $[22] = t4;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  return t13;
}
export function ShareCtas() {
  const $ = _c(24);
  const t = useTranslations("supportMainnet.share");
  let t0;
  if ($[0] !== t) {
    t0 = t("title");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <h2 id="share-heading" className="sr-only">{t0}</h2>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn(SHARE_SECTION_LEAD_MAX, "text-pretty text-heading-lg leading-8");
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("bodyLead");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <span className="font-medium text-foreground-primary">{t3}</span>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("bodyRest");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <span className="font-normal text-foreground-disabled">{t5}</span>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t6) {
    t7 = <p className={t2}>{t4}{t6}</p>;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t) {
    t8 = SHARE_CHANNELS.map((channel, index) => <li key={channel.id} className="h-full"><LandingSectionReveal className="h-full" delay={landingItemRevealDelay(index)}><ShareChannelCard channel={channel} description={t(`cards.${channel.id}.description`)} title={t(`cards.${channel.id}.title`)} /></LandingSectionReveal></li>);
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <ul className="grid grid-cols-1 gap-6 lg:grid-cols-2">{t8}</ul>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t1 || $[21] !== t7 || $[22] !== t9) {
    t10 = <section aria-labelledby="share-heading" className="flex flex-col gap-12">{t1}{t7}{t9}</section>;
    $[20] = t1;
    $[21] = t7;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}